(() => {
  'use strict';

  const STORAGE_KEY = 'alfajr_cod_last_order';
  const PURCHASE_KEY_PREFIX = 'alfajr_cod_purchase_';
  const STORAGE_MAX_AGE = 24 * 60 * 60 * 1000;
  const WINDOW_NAME_PREFIX = 'ALFAJR_COD_ORDER::';

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));

  function parseBoolean(value) {
    return String(value).toLowerCase() === 'true';
  }

  function cleanPhone(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function text(value, fallback = '—') {
    const normalized = String(value ?? '').trim();
    return normalized || fallback;
  }

  function number(value, fallback = 0) {
    if (value === null || value === undefined || String(value).trim() === '') return fallback;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const normalized = String(value)
      .replace(/\u00a0/g, ' ')
      .replace(/\s/g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function formatMAD(value) {
    const amount = number(value, 0);
    return `${new Intl.NumberFormat('fr-MA', {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount)} DH`;
  }

  function isFes(city) {
    const normalized = String(city || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');

    return ['fes', 'fez', 'فاس', 'فااس', 'فس'].includes(normalized);
  }

  function safeJsonParse(raw) {
    try { return JSON.parse(raw); } catch (_) { return null; }
  }

  function normalizeOrder(raw, root) {
    const payload = raw?.order || raw?.data || raw?.result || raw || {};
    const customer = payload.customer || payload.shippingAddress || payload.shipping_address || payload.delivery || {};
    const itemsSource = payload.items || payload.lineItems || payload.line_items || payload.products || [];
    const items = Array.isArray(itemsSource) ? itemsSource.map((item) => ({
      id: item.id || item.variant_id || item.variantId || item.product_id || '',
      title: item.title || item.name || item.product_title || item.productTitle || 'Article AL FAJR',
      variant: item.variant || item.variant_title || item.variantTitle || item.options || '',
      quantity: Math.max(1, number(item.quantity ?? item.qty, 1)),
      price: payload.bridgeVersion === '6.0.0' && /^Article \d+$/.test(String(item.title || '')) && !item.image && number(item.price, null) === 0
        ? null
        : number(item.price ?? item.unit_price ?? item.unitPrice ?? item.amount, null),
      image: item.image || item.image_url || item.imageUrl || item.featured_image || item.featuredImage || ''
    })) : [];

    const city = customer.city || customer.ville || payload.city || payload.ville || '';
    const computedSubtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const defaultShipping = isFes(city)
      ? number(root.dataset.defaultShippingFes, 20)
      : number(root.dataset.defaultShippingOther, 35);

    const subtotal = number(payload.subtotal ?? payload.subtotal_price ?? payload.subtotalPrice, computedSubtotal);
    const shipping = number(
      payload.shipping ?? payload.shipping_price ?? payload.shippingPrice ?? payload.delivery_fee ?? payload.deliveryFee ?? payload.frais_livraison,
      defaultShipping
    );
    const total = number(payload.total ?? payload.total_price ?? payload.totalPrice ?? payload.amount, subtotal + shipping);

    return {
      orderId: text(payload.orderId || payload.order_id || payload.id || payload.draftOrderId || '', ''),
      orderNumber: text(payload.orderNumber || payload.order_number || payload.reference || payload.order_name || payload.draftOrderName || payload.name || '', ''),
      token: text(payload.token || payload.statusToken || payload.status_token, ''),
      customer: {
        name: text(customer.name || customer.full_name || customer.fullName || customer.customer_name || payload.customer_name || payload.full_name || payload.nom_complet || '', ''),
        phone: text(customer.phone || customer.telephone || customer.tel || payload.phone || payload.telephone || payload.tel || '', ''),
        city: text(city, ''),
        address: text(customer.address || customer.address1 || customer.adresse || payload.address || payload.adresse || '', '')
      },
      items,
      subtotal,
      shipping,
      total,
      currency: payload.currency || payload.currencyCode || 'MAD',
      meta: {
        browserEnabled: payload.meta?.browserEnabled === true,
        pixelId: text(payload.meta?.pixelId, ''),
        eventId: text(payload.meta?.eventId, '')
      },
      capturedAt: number(payload.capturedAt, 0)
    };
  }

  function orderFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const rawItems = safeJsonParse(params.get('items') || '[]') || [];
    return {
      orderId: params.get('order_id') || params.get('id') || '',
      orderNumber: params.get('order') || params.get('order_number') || '',
      token: params.get('token') || '',
      customer: {
        name: params.get('name') || '',
        phone: params.get('phone') || '',
        city: params.get('city') || '',
        address: params.get('address') || ''
      },
      items: rawItems,
      subtotal: params.get('subtotal'),
      shipping: params.get('shipping'),
      total: params.get('total')
    };
  }

  async function loadRemoteOrder(root, seed) {
    const endpoint = String(root.dataset.ordersEndpoint || '').trim();
    if (!endpoint || (!seed.orderId && !seed.orderNumber)) return null;

    const url = new URL(endpoint, window.location.origin);
    if (seed.orderId) url.searchParams.set('order_id', seed.orderId);
    if (seed.orderNumber) url.searchParams.set('order', seed.orderNumber);
    if (seed.token) url.searchParams.set('token', seed.token);

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Order endpoint returned ${response.status}`);
    const body = await response.json();
    return body.order || body.data || body;
  }

  function mergeOrderData(base = {}, incoming = {}) {
    const merged = { ...base };
    for (const [key, value] of Object.entries(incoming || {})) {
      if (key === 'customer') {
        merged.customer = { ...(base.customer || {}) };
        for (const [customerKey, customerValue] of Object.entries(value || {})) {
          if (customerValue !== null && customerValue !== undefined && String(customerValue).trim() !== '') {
            merged.customer[customerKey] = customerValue;
          }
        }
      } else if (key === 'items') {
        if (Array.isArray(value) && value.length) merged.items = value;
      } else if (value !== null && value !== undefined && String(value).trim() !== '') {
        merged[key] = value;
      }
    }
    return merged;
  }

  function readStoredOrder() {
    let stored = null;
    try { stored = safeJsonParse(sessionStorage.getItem(STORAGE_KEY) || ''); } catch (_) {}
    if (!stored) {
      try { stored = safeJsonParse(localStorage.getItem(STORAGE_KEY) || ''); } catch (_) {}
    }
    if (!stored && String(window.name || '').startsWith(WINDOW_NAME_PREFIX)) {
      stored = safeJsonParse(String(window.name).slice(WINDOW_NAME_PREFIX.length));
    }
    if (!stored) return {};
    if (stored.capturedAt && Date.now() - Number(stored.capturedAt) > STORAGE_MAX_AGE) return {};
    return stored;
  }

  function readInitialOrder() {
    const queryOrder = orderFromQuery();
    const stored = readStoredOrder();
    const globalOrder = window.ALFAJR_COD_ORDER && typeof window.ALFAJR_COD_ORDER === 'object'
      ? window.ALFAJR_COD_ORDER
      : {};
    return mergeOrderData(mergeOrderData(stored, globalOrder), queryOrder);
  }

  function setText(root, selector, value) {
    const el = qs(root, selector);
    if (el) el.textContent = text(value);
  }

  function createProductRow(item) {
    const row = document.createElement('article');
    row.className = 'af-ty__product';

    const imageWrap = document.createElement('div');
    imageWrap.className = 'af-ty__product-image-wrap';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'af-ty__product-image';
      img.src = item.image;
      img.alt = item.title;
      img.loading = 'lazy';
      img.width = 84;
      img.height = 84;
      imageWrap.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'af-ty__product-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = '▣';
      imageWrap.appendChild(placeholder);
    }

    const qty = document.createElement('span');
    qty.className = 'af-ty__qty';
    qty.textContent = String(item.quantity);
    imageWrap.appendChild(qty);

    const info = document.createElement('div');
    info.className = 'af-ty__product-info';

    const title = document.createElement('h3');
    title.className = 'af-ty__product-title';
    title.textContent = item.title;
    info.appendChild(title);

    if (item.variant) {
      const variant = document.createElement('p');
      variant.className = 'af-ty__product-variant';
      variant.textContent = item.variant;
      info.appendChild(variant);
    }

    const price = document.createElement('div');
    price.className = 'af-ty__product-price';
    price.textContent = item.price === null ? 'À confirmer' : formatMAD(item.price * item.quantity);

    row.append(imageWrap, info, price);
    return row;
  }

  function renderProducts(root, items) {
    const container = qs(root, '[data-af-products]');
    if (!container) return;
    container.replaceChildren();
    container.classList.toggle('is-empty', !items.length);

    if (!items.length) {
      const empty = document.createElement('p');
      empty.className = 'af-ty__product-variant';
      empty.textContent = 'Les détails des articles seront confirmés par téléphone.';
      container.appendChild(empty);
      return;
    }

    items.forEach((item) => container.appendChild(createProductRow(item)));
  }

  function buildWhatsAppUrl(numberValue, message) {
    const phone = cleanPhone(numberValue);
    return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : '#';
  }

  function setupWhatsApp(root, order) {
    const numberValue = root.dataset.whatsappNumber;
    const orderLabel = order.orderNumber || order.orderId || '—';
    const correctionMessage = `Bonjour AL FAJR, je souhaite corriger les informations de ma commande Nº ${orderLabel}.`;
    const supportMessage = `Bonjour AL FAJR, j’ai besoin d’aide concernant ma commande Nº ${orderLabel}.`;

    qsa(root, '[data-af-whatsapp-correction]').forEach((link) => {
      link.href = buildWhatsAppUrl(numberValue, correctionMessage);
    });

    const support = qs(root, '[data-af-whatsapp-support]');
    if (support) support.href = buildWhatsAppUrl(numberValue, supportMessage);
  }

  function showToast(root, message) {
    const toast = qs(root, '[data-af-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.setTimeout(() => toast.classList.remove('is-visible'), 1800);
  }

  function setupCopy(root, order) {
    const button = qs(root, '[data-af-copy-order]');
    if (!button) return;
    button.addEventListener('click', async () => {
      const value = order.orderNumber || order.orderId;
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        showToast(root, 'Numéro de commande copié');
      } catch (_) {
        showToast(root, `Commande : ${value}`);
      }
    });
  }

  function trackingItems(order) {
    return order.items.map((item) => ({
      item_id: String(item.id || item.title),
      item_name: item.title,
      item_variant: item.variant || undefined,
      price: item.price,
      quantity: item.quantity
    }));
  }

  const trackedInPage = new Set();
  function wasTracked(key, session = false) {
    if (trackedInPage.has(key)) return true;
    try { return Boolean((session ? sessionStorage : localStorage).getItem(key)); } catch (_) { return false; }
  }
  function markTracked(key, session = false) {
    trackedInPage.add(key);
    try { (session ? sessionStorage : localStorage).setItem(key, String(Date.now())); } catch (_) {}
  }
  function processingAllowed(method) {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.[method] !== 'function' || privacy[method]();
    } catch (_) { return false; }
  }
  function ensureMetaPixel(pixelId) {
    if (!/^\d{5,30}$/.test(pixelId)) return false;
    if (typeof window.fbq !== 'function') {
      const fbq = function () { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
      fbq.queue = []; fbq.loaded = true; fbq.version = '2.0'; fbq.push = fbq;
      window.fbq = fbq; window._fbq ||= fbq;
      const script = document.createElement('script');
      script.async = true; script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    window.__alfajrMetaPixelIds ||= {};
    if (!window.__alfajrMetaPixelIds[pixelId]) {
      window.fbq('init', pixelId);
      window.__alfajrMetaPixelIds[pixelId] = true;
    }
    return true;
  }
  function firePurchaseOnce(root, order) {
    const uniqueId = order.orderId || order.orderNumber;
    if (!uniqueId || !order.total || !order.items.length || order.subtotal <= 0 || order.items.some((item) => item.price === null)) return;

    const items = trackingItems(order);
    const meta = order.meta || {};
    const metaKey = `${PURCHASE_KEY_PREFIX}${meta.eventId}`;
    const gaKey = `${PURCHASE_KEY_PREFIX}ga4_${uniqueId}`;

    // Only the confirmed order response can authorize Meta tracking. Reuse the
    // server event ID and the product form's marker to avoid counting twice.
    if (parseBoolean(root.dataset.fireMetaPurchase) && meta.browserEnabled && meta.eventId &&
        !wasTracked(metaKey, true) && !wasTracked(metaKey) && processingAllowed('marketingAllowed') && ensureMetaPixel(meta.pixelId)) {
      window.fbq('trackSingle', meta.pixelId, 'Purchase', {
        value: order.total,
        currency: 'MAD',
        content_ids: items.map((item) => item.item_id),
        contents: order.items.map((item) => ({ id: String(item.id || item.title), quantity: item.quantity, item_price: item.price })),
        content_type: 'product',
        order_id: uniqueId
      }, { eventID: meta.eventId });
      markTracked(metaKey, true);
      markTracked(metaKey);
    }

    if (parseBoolean(root.dataset.fireGa4Purchase) && !wasTracked(gaKey) && processingAllowed('analyticsProcessingAllowed') && typeof window.gtag === 'function') {
      window.gtag('event', 'purchase', {
        transaction_id: uniqueId,
        value: order.total,
        tax: 0,
        shipping: order.shipping,
        currency: 'MAD',
        items
      });
      markTracked(gaKey);
    }
  }

  function render(root, order) {
    const label = order.orderNumber || order.orderId || 'En cours de création';
    setText(root, '[data-af-order-number]', label);
    setText(root, '[data-af-customer-name]', order.customer.name);
    setText(root, '[data-af-customer-phone]', order.customer.phone);
    setText(root, '[data-af-customer-city]', order.customer.city);
    setText(root, '[data-af-customer-address]', order.customer.address);
    const hasPricing = order.items.length > 0 && order.subtotal > 0;
    setText(root, '[data-af-subtotal]', hasPricing ? formatMAD(order.subtotal) : 'À confirmer');
    setText(root, '[data-af-shipping]', hasPricing ? formatMAD(order.shipping) : 'À confirmer');
    setText(root, '[data-af-total]', hasPricing ? formatMAD(order.total) : 'À confirmer');

    const shippingLabel = qs(root, '[data-af-shipping-label]');
    if (shippingLabel) shippingLabel.textContent = order.customer.city ? `Livraison à ${order.customer.city}` : 'Livraison';

    renderProducts(root, order.items);
    setupWhatsApp(root, order);
    setupCopy(root, order);
    firePurchaseOnce(root, order);
  }

  async function init(root) {
    const seed = normalizeOrder(readInitialOrder(), root);
    let order = seed;

    try {
      const remote = await loadRemoteOrder(root, seed);
      if (remote) {
        order = normalizeOrder(remote, root);
        const remotePayload = JSON.stringify({ ...remote, capturedAt: Date.now() });
        try { sessionStorage.setItem(STORAGE_KEY, remotePayload); } catch (_) {}
        try { localStorage.setItem(STORAGE_KEY, remotePayload); } catch (_) {}
      }
    } catch (error) {
      console.warn('[AL FAJR COD] Could not reload order data:', error);
    }

    render(root, order);
  }

  function boot() {
    document.querySelectorAll('.af-ty').forEach((root) => init(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
